import { z } from 'zod';
import {
  EQUIPMENT_COMPONENTS,
  EQUIPMENT_MAX_LEVEL,
  type EquipmentAttribute,
  type EquipmentComponent,
} from './catalog';
import { effectiveAttributeValue } from './levels';
import type { EquipmentRoll } from './roll';

/**
 * What the five equipped components add up to.
 *
 * ## Equipped, and only equipped
 *
 * This is the single difference from an artifact, and it is enforced by the
 * shape of the input: this function takes the FIVE SLOTS, not an inventory. An
 * unequipped component contributes nothing, whoever owns it and however good it
 * is, because it never reaches this function at all.
 *
 * ## Exact integers, summed once
 *
 * Every total is a `bigint` in the attribute's own sub-unit, and each item's
 * contribution is derived from its immutable roll at its current level
 * (`effectiveAttributeValue`) before being added. Percentages are ADDED, never
 * multiplied together: five components carrying +9% damage are +45%, and the
 * result cannot depend on the order the slots happen to be read in.
 */

/** The nine sums, in the units of `EQUIPMENT_ATTRIBUTE_UNITS`. */
export interface EquipmentTotals {
  /** MILLI-damage, applied AFTER the percentage multiplier. */
  readonly bonusAttackMilli: bigint;
  /** MILLI-damage, added to the base BEFORE the percentage multiplier. */
  readonly baseAttackMilli: bigint;
  readonly attackBps: bigint;
  readonly baseAttackSpeedMicro: bigint;
  readonly attackSpeedBps: bigint;
  readonly maxHealthMilli: bigint;
  readonly maxHealthBps: bigint;
  readonly goldPerEnemyMilli: bigint;
  readonly goldBps: bigint;
}

export const NO_EQUIPMENT_TOTALS: EquipmentTotals = Object.freeze({
  bonusAttackMilli: 0n,
  baseAttackMilli: 0n,
  attackBps: 0n,
  baseAttackSpeedMicro: 0n,
  attackSpeedBps: 0n,
  maxHealthMilli: 0n,
  maxHealthBps: 0n,
  goldPerEnemyMilli: 0n,
  goldBps: 0n,
});

/** Which total each attribute feeds. One place, so nothing can feed two. */
const TOTAL_KEY_BY_ATTRIBUTE: Readonly<Record<EquipmentAttribute, keyof EquipmentTotals>> =
  Object.freeze({
    bonusAttack: 'bonusAttackMilli',
    baseAttack: 'baseAttackMilli',
    attackPercent: 'attackBps',
    baseAttackSpeed: 'baseAttackSpeedMicro',
    attackSpeedPercent: 'attackSpeedBps',
    flatMaxHealth: 'maxHealthMilli',
    maxHealthPercent: 'maxHealthBps',
    goldPerEnemy: 'goldPerEnemyMilli',
    goldPercent: 'goldBps',
  });

/** One equipped component, as the resolver receives it. */
export interface EquippedComponent {
  readonly instanceId: string;
  readonly definitionId: string;
  readonly component: EquipmentComponent;
  readonly level: number;
  readonly roll: EquipmentRoll;
}

/** Every effective value of ONE component at its current level. */
export function componentContribution(item: EquippedComponent): EquipmentTotals {
  let totals: EquipmentTotals = NO_EQUIPMENT_TOTALS;
  for (const entry of item.roll.attributes) {
    const key = TOTAL_KEY_BY_ATTRIBUTE[entry.attribute];
    const value = effectiveAttributeValue(BigInt(entry.baseValue), item.level);
    totals = { ...totals, [key]: totals[key] + value };
  }
  return totals;
}

export function addEquipmentTotals(a: EquipmentTotals, b: EquipmentTotals): EquipmentTotals {
  return {
    bonusAttackMilli: a.bonusAttackMilli + b.bonusAttackMilli,
    baseAttackMilli: a.baseAttackMilli + b.baseAttackMilli,
    attackBps: a.attackBps + b.attackBps,
    baseAttackSpeedMicro: a.baseAttackSpeedMicro + b.baseAttackSpeedMicro,
    attackSpeedBps: a.attackSpeedBps + b.attackSpeedBps,
    maxHealthMilli: a.maxHealthMilli + b.maxHealthMilli,
    maxHealthBps: a.maxHealthBps + b.maxHealthBps,
    goldPerEnemyMilli: a.goldPerEnemyMilli + b.goldPerEnemyMilli,
    goldBps: a.goldBps + b.goldBps,
  };
}

/** The five slots summed. Empty slots contribute nothing, by construction. */
export function equipmentTotals(equipped: readonly EquippedComponent[]): EquipmentTotals {
  return equipped.reduce(
    (totals, item) => addEquipmentTotals(totals, componentContribution(item)),
    NO_EQUIPMENT_TOTALS,
  );
}

/* -------------------------------------------------------------------------- */
/*  Persistence                                                               */
/* -------------------------------------------------------------------------- */

/**
 * The frozen summary a hunt session carries, exactly as it carries its loadout
 * and its artifact passives.
 *
 * Same rule and same reason: a player who equips, removes or upgrades a
 * component mid-window must not retroactively change what the seconds already
 * played were worth. It holds the instance ids, the components, the levels and
 * the totals - enough to re-simulate a window exactly and to prove which items
 * were live when a reward was credited. Bigints cross as decimal strings, like
 * every other unbounded value in this project.
 */
export const equipmentSummarySchema = z.object({
  /** Bumped when the meaning of the equipment catalog changes. */
  contentVersion: z.number().int().min(1),
  entries: z.array(
    z.object({
      slot: z.string().min(1),
      instanceId: z.uuid(),
      definitionId: z.string().min(1),
      level: z.number().int().min(1).max(EQUIPMENT_MAX_LEVEL),
    }),
  ),
  totals: z.object({
    bonusAttackMilli: z.string(),
    baseAttackMilli: z.string(),
    attackBps: z.string(),
    baseAttackSpeedMicro: z.string(),
    attackSpeedBps: z.string(),
    maxHealthMilli: z.string(),
    maxHealthBps: z.string(),
    goldPerEnemyMilli: z.string(),
    goldBps: z.string(),
  }),
});
export type EquipmentSummary = z.infer<typeof equipmentSummarySchema>;

/** Version of the equipment catalog's MEANING, not of the code. */
export const EQUIPMENT_CONTENT_VERSION = 1;

export function toEquipmentSummary(
  equipped: readonly EquippedComponent[],
  totals: EquipmentTotals = equipmentTotals(equipped),
): EquipmentSummary {
  return {
    contentVersion: EQUIPMENT_CONTENT_VERSION,
    // Sorted by the fixed component order so the summary is byte-stable: it
    // goes into a session row, and a reordering would read as a state change.
    entries: [...equipped]
      .sort(
        (a, b) =>
          EQUIPMENT_COMPONENTS.indexOf(a.component) - EQUIPMENT_COMPONENTS.indexOf(b.component),
      )
      .map((item) => ({
        slot: `equipment_${item.component}`,
        instanceId: item.instanceId,
        definitionId: item.definitionId,
        level: item.level,
      })),
    totals: {
      bonusAttackMilli: totals.bonusAttackMilli.toString(10),
      baseAttackMilli: totals.baseAttackMilli.toString(10),
      attackBps: totals.attackBps.toString(10),
      baseAttackSpeedMicro: totals.baseAttackSpeedMicro.toString(10),
      attackSpeedBps: totals.attackSpeedBps.toString(10),
      maxHealthMilli: totals.maxHealthMilli.toString(10),
      maxHealthBps: totals.maxHealthBps.toString(10),
      goldPerEnemyMilli: totals.goldPerEnemyMilli.toString(10),
      goldBps: totals.goldBps.toString(10),
    },
  };
}

export const EMPTY_EQUIPMENT_SUMMARY: EquipmentSummary = Object.freeze(toEquipmentSummary([]));

/** Reads a persisted summary back into exact bigints. */
export function totalsFromSummary(summary: EquipmentSummary): EquipmentTotals {
  return {
    bonusAttackMilli: BigInt(summary.totals.bonusAttackMilli),
    baseAttackMilli: BigInt(summary.totals.baseAttackMilli),
    attackBps: BigInt(summary.totals.attackBps),
    baseAttackSpeedMicro: BigInt(summary.totals.baseAttackSpeedMicro),
    attackSpeedBps: BigInt(summary.totals.attackSpeedBps),
    maxHealthMilli: BigInt(summary.totals.maxHealthMilli),
    maxHealthBps: BigInt(summary.totals.maxHealthBps),
    goldPerEnemyMilli: BigInt(summary.totals.goldPerEnemyMilli),
    goldBps: BigInt(summary.totals.goldBps),
  };
}

export function parseEquipmentSummary(value: unknown): EquipmentSummary {
  return equipmentSummarySchema.parse(value);
}

/**
 * A stable, readable fingerprint of the equipment an epoch was earned under.
 *
 * `e:{slot}={definition}@{level}:{instanceId}` per equipped component, in the
 * fixed component order. The INSTANCE id is in there for the same reason it is
 * in the loadout fingerprint: swapping a component for an identical copy
 * produces a different economic object, and an offline average must not carry
 * over. Upgrading changes the level, which changes this, which starts a fresh
 * averaging epoch - exactly right, because the earning rate really did change.
 */
export function equipmentFingerprint(summary: EquipmentSummary): string {
  if (summary.entries.length === 0) {
    return 'eq:none';
  }
  return summary.entries
    .map((entry) => `eq:${entry.slot}=${entry.definitionId}@${entry.level}:${entry.instanceId}`)
    .join(',');
}

/* -------------------------------------------------------------------------- */
/*  Equipment totals, on the wire                                             */
/* -------------------------------------------------------------------------- */

/**
 * The nine equipment sums a session is FROZEN to, as decimal strings.
 *
 * ## Why this exists
 *
 * The browser simulates the hunt and the server re-simulates it, and the two
 * have to agree. Everything else the run is bound to already travels for that
 * reason - the loadout, the artifact totals, the active pet - and equipment did
 * not. The client had no way to know what its components were worth, so it
 * simulated as though the account had none: the ship in the arena dealt base
 * damage while the server credited the real figure, and every window ended in a
 * digest mismatch and a correction.
 *
 * ## Strings, like every other exact integer here
 *
 * These are `bigint` sums in milli-damage, micro-attacks and basis points. A
 * JSON number would be exact for a while and then quietly stop being, which is
 * the failure this project routes every large integer through decimal strings
 * to avoid. `equipmentTotalsFromWire` parses them back with `BigInt`, never
 * through `Number`.
 */
/**
 * The same canonical-decimal rule `contracts/primitives` states, restated here.
 *
 * Not imported: this module lives under `equipment/`, and `contracts/` already
 * imports from it - taking the schema the other way would close a cycle
 * between two modules that both build zod objects at load time.
 */
const exactIntegerString = z
  .string()
  .min(1)
  .max(40)
  .regex(/^(0|[1-9][0-9]*)$/, 'must be a canonical non-negative decimal');

export const equipmentTotalsSchema = z.object({
  bonusAttackMilli: exactIntegerString,
  baseAttackMilli: exactIntegerString,
  attackBps: exactIntegerString,
  baseAttackSpeedMicro: exactIntegerString,
  attackSpeedBps: exactIntegerString,
  maxHealthMilli: exactIntegerString,
  maxHealthBps: exactIntegerString,
  goldPerEnemyMilli: exactIntegerString,
  goldBps: exactIntegerString,
});
export type EquipmentTotalsWire = z.infer<typeof equipmentTotalsSchema>;

/** Totals as the server holds them, ready for the wire. */
export function toEquipmentTotalsWire(totals: EquipmentTotals): EquipmentTotalsWire {
  return {
    bonusAttackMilli: totals.bonusAttackMilli.toString(),
    baseAttackMilli: totals.baseAttackMilli.toString(),
    attackBps: totals.attackBps.toString(),
    baseAttackSpeedMicro: totals.baseAttackSpeedMicro.toString(),
    attackSpeedBps: totals.attackSpeedBps.toString(),
    maxHealthMilli: totals.maxHealthMilli.toString(),
    maxHealthBps: totals.maxHealthBps.toString(),
    goldPerEnemyMilli: totals.goldPerEnemyMilli.toString(),
    goldBps: totals.goldBps.toString(),
  };
}

/** Wire totals back into the exact bigints the simulation is configured with. */
export function equipmentTotalsFromWire(wire: EquipmentTotalsWire): EquipmentTotals {
  return {
    bonusAttackMilli: BigInt(wire.bonusAttackMilli),
    baseAttackMilli: BigInt(wire.baseAttackMilli),
    attackBps: BigInt(wire.attackBps),
    baseAttackSpeedMicro: BigInt(wire.baseAttackSpeedMicro),
    attackSpeedBps: BigInt(wire.attackSpeedBps),
    maxHealthMilli: BigInt(wire.maxHealthMilli),
    maxHealthBps: BigInt(wire.maxHealthBps),
    goldPerEnemyMilli: BigInt(wire.goldPerEnemyMilli),
    goldBps: BigInt(wire.goldBps),
  };
}
